import React, { useContext } from "react";
import { useIntl } from "react-intl";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ICategory from "../../../../../models/ICategory";
import UiContext from "../../../../../contexts/UiContext";
import OwButton from "../../../../shared/OwButton/OwButton";
import SimpleConfirmModal from "../../../../shared/SimpleConfirmModal/SimpleConfirmModal";
import "./SortableCategoryList.scss";

/**
 * Sibling categories ordered A-Z by name: case-insensitive, with numbers compared by value
 * ("Size 2" before "Size 10").
 */
export const sortCategoriesByName = (categories: ICategory[]): ICategory[] => {
    return [...categories].sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: "base" }));
};

/**
 * Maps a drag-end event's active/over ids to indices within the sortable guid list.
 * Returns null when the drop should be ignored (no movement, or unknown ids).
 */
export const resolveReorder = (itemGuids: string[], activeId: string, overId?: string): { oldIndex: number; newIndex: number } | null => {
    if (!overId || activeId === overId) {
        return null;
    }

    const oldIndex = itemGuids.indexOf(activeId);
    const newIndex = itemGuids.indexOf(overId);

    if (oldIndex === -1 || newIndex === -1) {
        return null;
    }

    return { oldIndex, newIndex };
};

type ListProps = {
    // Saved sibling guids in their current display order
    itemGuids: string[];
    disabled?: boolean;
    onReorder: (oldIndex: number, newIndex: number) => void;
    children: React.ReactNode;
};

const SortableCategoryList: React.FC<ListProps> = ({ itemGuids, disabled, onReorder, children }) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        const result = resolveReorder(itemGuids, active.id as string, over?.id as string | undefined);

        if (result) {
            onReorder(result.oldIndex, result.newIndex);
        }
    };

    if (disabled) {
        return <>{children}</>;
    }

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={itemGuids} strategy={verticalListSortingStrategy}>
                {children}
            </SortableContext>
        </DndContext>
    );
};

type ItemProps = {
    guid: string;
    disabled?: boolean;
    children: React.ReactNode;
};

export const SortableCategoryItem: React.FC<ItemProps> = ({ guid, disabled, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: guid, disabled });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div ref={setNodeRef} style={style} className={`SortableCategoryItem d-flex align-items-start ${isDragging ? "is-dragging" : ""}`}>
            {!disabled && (
                <div {...attributes} {...listeners} className="p-2 drag-handle" style={{ cursor: "grab" }}>
                    <i className="fas fa-grip-vertical text-muted" />
                </div>
            )}
            <div className="flex-grow-1 min-width-0">{children}</div>
        </div>
    );
};

type SortByNameButtonProps = {
    disabled?: boolean;
    // Icon-only (with a tooltip) for tight headers like the subcategory row
    iconOnly?: boolean;
    className?: string;
    // Called once the user confirms; applies and saves the A-Z order
    onConfirm: () => void;
};

/**
 * "Sort A-Z" button that asks for confirmation before replacing a sibling group's saved order.
 */
export const SortCategoriesByNameButton: React.FC<SortByNameButtonProps> = ({ disabled, iconOnly, className, onConfirm }) => {
    const uiContext = useContext(UiContext);
    const intl = useIntl();
    const label = intl.formatMessage({ id: "sort_a_z" });

    const closeModal = () => {
        uiContext.setModalComponent(null);
        uiContext.showModal(false);
    };

    const confirmSort = () => {
        closeModal();
        onConfirm();
    };

    const showConfirm = () => {
        uiContext.setModalComponent(
            <SimpleConfirmModal
                message={"confirm_sort_categories_by_name"}
                subMessage={"confirm_sort_categories_by_name_detail"}
                confirmTitle={"sort_a_z"}
                cancelFn={closeModal}
                confirmFn={confirmSort}
            />
        );
        uiContext.showModal(true);
    };

    if (iconOnly) {
        return (
            <OwButton
                centerIconClass={"far fa-sort-alpha-down"}
                className={className}
                fn={showConfirm}
                disabled={disabled}
                ariaLabel={label}
                hoverTitle={label}
            />
        );
    }

    return (
        <OwButton title={"sort_a_z"} leftIconClass={"far fa-sort-alpha-down"} className={className} fn={showConfirm} disabled={disabled} />
    );
};

export default SortableCategoryList;
